import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BundleService } from '@admin-core/services/bundle.service';
import { BrandService } from '@admin-core/services/brand.service';
import { CategoryService } from '@admin-core/services/category.service';
import { FilterService } from '@admin-core/services/filter.service';
import { ProductService } from '@admin-core/services/product.service';
import { Bundle, BundleListItem, BundleProduct } from '@admin-core/models/bundle.model';
import { ProductAttributeValue, ProductListItem } from '@admin-core/models/product.model';
import { Brand } from '@admin-core/models/brand.model';
import { Category } from '@admin-core/models/category.model';
import { FilterValueType } from '@admin-core/models/filter.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { formatMoney } from '@admin-shared/utils/money.util';
import { slugify } from '@admin-shared/utils/slug.util';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';

interface AttributeField {
  filterId: string;
  name: string;
  valueType: FilterValueType;
  value: string;
  options: { value: string; display: string }[];
}

type ProductPickRole = 'required' | 'optional';

@Component({
  selector: 'app-bundles-admin',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, TranslatePipe],
  templateUrl: './bundles-admin.component.html',
  styleUrl: './bundles-admin.component.scss'
})
export class BundlesAdminComponent implements OnInit {
  private readonly api = inject(BundleService);
  private readonly productsApi = inject(ProductService);
  private readonly brandsApi = inject(BrandService);
  private readonly categoriesApi = inject(CategoryService);
  private readonly filtersApi = inject(FilterService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<BundleListItem[]>([]);
  readonly brands = signal<Brand[]>([]);
  readonly leafCategories = signal<Category[]>([]);
  readonly categoryOptions = signal<{ id: string; label: string }[]>([]);
  readonly brandOptions = signal<{ id: string; name: string }[]>([]);
  readonly attributeFields = signal<AttributeField[]>([]);
  readonly requiredProducts = signal<BundleProduct[]>([]);
  readonly optionalProducts = signal<BundleProduct[]>([]);
  readonly productResults = signal<ProductListItem[]>([]);
  readonly productSearch = signal('');
  readonly loading = signal(false);
  readonly productsLoading = signal(false);
  readonly dialogOpen = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly filterBrandId = signal('');
  readonly filterCategoryId = signal('');

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(200)]],
    slug: ['', [Validators.required, Validators.maxLength(200)]],
    brandId: ['', Validators.required],
    categoryId: ['', Validators.required],
    description: [''],
    price: [0, [Validators.required, Validators.min(0)]],
    compareAtPrice: this.fb.control<number | null>(null),
    isActive: [true]
  });

  readonly selectedProductIds = computed(() => {
    const ids = new Set<string>();
    for (const p of this.requiredProducts()) ids.add(p.productId);
    for (const p of this.optionalProducts()) ids.add(p.productId);
    return ids;
  });

  readonly availableProductResults = computed(() => {
    const selected = this.selectedProductIds();
    return this.productResults().filter((p) => !selected.has(p.id) && p.isActive);
  });

  ngOnInit(): void {
    this.brandsApi.getAll().subscribe({
      next: (items) => this.brands.set(items),
      error: () => this.brands.set([])
    });

    this.categoriesApi.getTree().subscribe({
      next: (tree) => {
        const leaves: Category[] = [];
        for (const parent of tree) {
          for (const child of parent.children ?? []) {
            leaves.push(child);
          }
        }
        this.leafCategories.set(leaves);
      },
      error: () => this.leafCategories.set([])
    });

    this.loadFilterOptions();
    this.load();
  }

  money(value: number): string {
    return formatMoney(value);
  }

  onFilterChange(): void {
    this.load();
  }

  private loadFilterOptions(): void {
    this.api.getFilterOptions().subscribe({
      next: (opts) => {
        this.categoryOptions.set(
          (opts.categories ?? []).map((c) => ({
            id: c.id,
            label: c.parentName ? `${c.parentName} / ${c.name}` : c.name
          }))
        );
        this.brandOptions.set(
          (opts.brands ?? []).map((b) => ({ id: b.id, name: b.name }))
        );
      },
      error: () => {
        this.categoryOptions.set([]);
        this.brandOptions.set([]);
      }
    });
  }

  load(): void {
    this.loading.set(true);
    this.api
      .getPaged({
        page: 1,
        pageSize: 100,
        brandId: this.filterBrandId() || undefined,
        categoryId: this.filterCategoryId() || undefined
      })
      .subscribe({
        next: (res) => {
          this.items.set(res.items);
          this.loading.set(false);
        },
        error: (err) => {
          this.showError(err?.error?.error ?? this.i18n.t('bundles.loadError'));
          this.loading.set(false);
        }
      });
  }

  openCreate(): void {
    this.editingId.set(null);
    this.requiredProducts.set([]);
    this.optionalProducts.set([]);
    this.productResults.set([]);
    this.productSearch.set('');
    this.form.reset({
      name: '',
      slug: '',
      brandId: this.brands()[0]?.id ?? '',
      categoryId: this.leafCategories()[0]?.id ?? '',
      description: '',
      price: 0,
      compareAtPrice: null,
      isActive: true
    });
    this.dialogOpen.set(true);
    const categoryId = this.form.controls.categoryId.value;
    if (categoryId) {
      this.loadAttributeFields(categoryId);
    } else {
      this.attributeFields.set([]);
    }
    this.searchProducts();
  }

  openEdit(item: BundleListItem): void {
    this.editingId.set(item.id);
    this.api.getById(item.id).subscribe({
      next: (bundle) => this.populateForm(bundle),
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('bundles.loadError'))
    });
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
    this.attributeFields.set([]);
    this.requiredProducts.set([]);
    this.optionalProducts.set([]);
    this.productResults.set([]);
    this.productSearch.set('');
  }

  onNameChange(value: string): void {
    if (!this.editingId()) {
      this.form.controls.slug.setValue(slugify(value));
    }
  }

  onCategoryChange(): void {
    const categoryId = this.form.controls.categoryId.value;
    if (categoryId) {
      this.loadAttributeFields(categoryId);
    } else {
      this.attributeFields.set([]);
    }
  }

  setAttributeValue(filterId: string, value: string): void {
    this.attributeFields.update((fields) =>
      fields.map((f) => (f.filterId === filterId ? { ...f, value } : f))
    );
  }

  onProductSearch(value: string): void {
    this.productSearch.set(value);
    this.searchProducts();
  }

  addProduct(product: ProductListItem, role: ProductPickRole): void {
    if (this.selectedProductIds().has(product.id)) {
      return;
    }

    const entry: BundleProduct = {
      productId: product.id,
      name: product.name,
      slug: product.slug,
      sku: product.sku,
      price: product.price,
      brandId: product.brandId,
      brandName: product.brandName,
      isActive: product.isActive
    };

    if (role === 'required') {
      this.requiredProducts.update((list) => [...list, entry]);
    } else {
      this.optionalProducts.update((list) => [...list, entry]);
    }
  }

  removeProduct(productId: string, role: ProductPickRole): void {
    if (role === 'required') {
      this.requiredProducts.update((list) => list.filter((p) => p.productId !== productId));
    } else {
      this.optionalProducts.update((list) => list.filter((p) => p.productId !== productId));
    }
  }

  moveProduct(productId: string, from: ProductPickRole): void {
    const source = from === 'required' ? this.requiredProducts() : this.optionalProducts();
    const product = source.find((p) => p.productId === productId);
    if (!product) {
      return;
    }
    this.removeProduct(productId, from);
    if (from === 'required') {
      this.optionalProducts.update((list) => [...list, product]);
    } else {
      this.requiredProducts.update((list) => [...list, product]);
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.requiredProducts().length === 0) {
      this.showError(this.i18n.t('bundles.requiredProductsError'));
      return;
    }

    const raw = this.form.getRawValue();
    const payload = {
      name: raw.name,
      slug: raw.slug,
      description: raw.description,
      price: Number(raw.price),
      compareAtPrice:
        raw.compareAtPrice === null || raw.compareAtPrice === undefined
          ? null
          : Number(raw.compareAtPrice),
      categoryId: raw.categoryId,
      brandId: raw.brandId,
      isActive: raw.isActive,
      requiredProductIds: this.requiredProducts().map((p) => p.productId),
      optionalProductIds: this.optionalProducts().map((p) => p.productId),
      attributes: this.buildAttributePayload()
    };

    const id = this.editingId();
    const req$ = id ? this.api.update(id, payload) : this.api.create(payload);

    req$.subscribe({
      next: () => {
        this.closeDialog();
        this.loadFilterOptions();
        this.load();
      },
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('bundles.saveError'))
    });
  }

  async remove(item: BundleListItem): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: this.i18n.t('common.confirmTitle'),
      message: this.i18n.t('bundles.deleteConfirm', { name: item.name }),
      confirmLabel: this.i18n.t('common.delete'),
      danger: true
    });
    if (!ok) {
      return;
    }

    this.api.delete(item.id).subscribe({
      next: () => {
        this.loadFilterOptions();
        this.load();
      },
      error: (err) => this.showError(err?.error?.error ?? this.i18n.t('bundles.deleteError'))
    });
  }

  private populateForm(bundle: Bundle): void {
    this.form.setValue({
      name: bundle.name,
      slug: bundle.slug,
      brandId: bundle.brandId,
      categoryId: bundle.categoryId,
      description: bundle.description,
      price: bundle.price,
      compareAtPrice: bundle.compareAtPrice,
      isActive: bundle.isActive
    });
    this.requiredProducts.set([...(bundle.requiredProducts ?? [])]);
    this.optionalProducts.set([...(bundle.optionalProducts ?? [])]);
    this.productSearch.set('');
    this.dialogOpen.set(true);
    this.loadAttributeFields(bundle.categoryId, bundle.attributes);
    this.searchProducts();
  }

  private searchProducts(): void {
    this.productsLoading.set(true);
    this.productsApi
      .getPaged({
        page: 1,
        pageSize: 30,
        search: this.productSearch().trim() || undefined
      })
      .subscribe({
        next: (res) => {
          this.productResults.set(res.items);
          this.productsLoading.set(false);
        },
        error: () => {
          this.productResults.set([]);
          this.productsLoading.set(false);
        }
      });
  }

  private loadAttributeFields(
    categoryId: string,
    existing: ProductAttributeValue[] = []
  ): void {
    this.filtersApi.getCategoryFilters(categoryId, { includeAllOptions: true }).subscribe({
      next: (res) => {
        const byFilterId = new Map(existing.map((a) => [a.filterId, a]));
        const fields = (res.filters ?? [])
          .filter((f) => !this.isSystemManagedFilter(f.name))
          .map((f) => {
            const current = byFilterId.get(f.id);
            return {
              filterId: f.id,
              name: f.name,
              valueType: this.normalizeValueType(f.valueType),
              value: current ? this.attributeToInput(current) : '',
              options: (f.values ?? []).map((v) => ({
                value: String(v.value),
                display: v.display
              }))
            } satisfies AttributeField;
          });
        this.attributeFields.set(fields);
      },
      error: () => this.attributeFields.set([])
    });
  }

  private isSystemManagedFilter(name: string): boolean {
    const key = name.toLowerCase();
    return key === 'brand' || key === 'price';
  }

  private normalizeValueType(value: FilterValueType | number): FilterValueType {
    if (typeof value === 'string') {
      return value;
    }
    const map: FilterValueType[] = ['Guid', 'Text', 'Number', 'Boolean', 'Options'];
    return map[value] ?? 'Text';
  }

  private attributeToInput(attr: ProductAttributeValue): string {
    const type = this.normalizeValueType(attr.valueType as FilterValueType | number);
    switch (type) {
      case 'Guid':
        return attr.guidValue ?? '';
      case 'Number':
        return attr.numberValue == null ? '' : String(attr.numberValue);
      case 'Boolean':
        return attr.booleanValue == null ? '' : String(attr.booleanValue);
      case 'Options':
      case 'Text':
      default:
        return attr.textValue ?? '';
    }
  }

  private buildAttributePayload() {
    return this.attributeFields()
      .filter((f) => f.value.trim() !== '')
      .map((f) => {
        const value = f.value.trim();
        switch (f.valueType) {
          case 'Guid':
            return { filterId: f.filterId, guidValue: value };
          case 'Number':
            return { filterId: f.filterId, numberValue: Number(value) };
          case 'Boolean':
            return {
              filterId: f.filterId,
              booleanValue: value.toLowerCase() === 'true' || value === '1'
            };
          case 'Options':
          case 'Text':
          default:
            return { filterId: f.filterId, textValue: value };
        }
      });
  }

  private showError(message: string): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message,
      danger: true
    });
  }
}
